import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Container, NbCard, NbButton, Chip } from "@/components/nb";
import MeghalayaMap from "@/components/MeghalayaMap";
import {
  Search, AlertTriangle, Trophy, Lightbulb, MessageSquarePlus,
  ThumbsUp, Calendar, ArrowRight, MapPin, Box,
} from "lucide-react";
import { toast } from "sonner";

const REGION_CLS = {
  "Garo Hills": "bg-sl-primary",
  "Khasi Hills": "bg-sl-secondary",
  "Jaintia Hills": "bg-sl-accent",
};

export default function Meghalaya() {
  const [districts, setDistricts] = useState([]);
  const [active, setActive] = useState(null);
  const [q, setQ] = useState("");
  const [prompts, setPrompts] = useState([]);
  const [ideas, setIdeas] = useState([]);
  const [promptId, setPromptId] = useState("");
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [tilted, setTilted] = useState(false);
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    api.get("/meghalaya/districts").then((r) => {
      setDistricts(r.data);
      if (r.data[0]) setActive(r.data[0]);
    });
    api.get("/meghalaya/prompts").then((r) => {
      setPrompts(r.data);
      if (r.data[0]) setPromptId(r.data[0].id);
    });
  }, []);

  useEffect(() => {
    if (!active) return;
    api.get(`/meghalaya/ideas?district_id=${active.id}`).then((r) => setIdeas(r.data));
  }, [active]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return districts;
    return districts.filter((d) =>
      d.name.toLowerCase().includes(s) ||
      d.capital.toLowerCase().includes(s) ||
      d.region.toLowerCase().includes(s) ||
      [...d.problems, ...d.achievements, ...d.initiatives.map((i) => i.title || i)].join(" ").toLowerCase().includes(s)
    );
  }, [districts, q]);

  const submitIdea = async () => {
    if (!user) return toast.error("Sign in to share your idea");
    if (!body.trim()) return toast.error("Type your idea");
    if (!active || !promptId) return;
    setSubmitting(true);
    try {
      const r = await api.post("/meghalaya/ideas", { district_id: active.id, prompt_id: promptId, body });
      setIdeas((list) => [r.data, ...list]);
      setBody("");
      toast.success("Shared with the corner!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Post failed");
    } finally { setSubmitting(false); }
  };

  const vote = async (id) => {
    if (!user) return toast.error("Sign in to vote");
    try {
      const r = await api.post(`/meghalaya/ideas/${id}/vote`);
      setIdeas((list) => list.map((i) => (i.id === id ? r.data : i)));
    } catch { toast.error("Vote failed"); }
  };

  const activePrompt = prompts.find((p) => p.id === promptId);

  const onMapHover = (id) => {
    if (!id) return;
    const d = districts.find((x) => x.id === id);
    if (d) setActive(d);
  };
  const onMapSelect = (id) => nav(`/meghalaya/${id}`);

  return (
    <Container>
      {/* Compact header */}
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div>
          <div className="inline-block font-mono-sl text-[10px] uppercase tracking-widest bg-sl-tertiary nb-border rounded-full px-3 py-1 mb-2">MEGHALAYA · 2022–2026 · LIVE</div>
          <h1 className="font-heading font-black text-4xl md:text-5xl tracking-tighter leading-[0.95]">Meghalaya, district by district.</h1>
          <p className="mt-1 text-neutral-700 max-w-2xl">Real state map with real district borders. Hover any district for the live weather, or tap it to dive into the problems, wins and news of that region.</p>
        </div>
        <div className="flex items-center gap-2 bg-white nb-border nb-shadow rounded-xl px-3 py-2 max-w-md w-full md:w-72">
          <Search className="w-4 h-4 shrink-0" />
          <input data-testid="megh-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder='Search "coal", "elephant", "Tura"...' className="flex-1 bg-transparent outline-none text-sm py-0.5" />
          {q && <button onClick={() => setQ("")} data-testid="megh-clear" className="font-mono-sl text-[10px] uppercase underline">Clear</button>}
        </div>
      </div>

      {q && (
        <NbCard className="!p-3 mb-4" data-testid="megh-search-results">
          <div className="font-mono-sl text-[10px] uppercase mb-2">MATCHES · {filtered.length}</div>
          <div className="flex flex-wrap gap-2">
            {filtered.map((d) => (
              <button key={d.id} onClick={() => setActive(d)} data-testid={`megh-result-${d.id}`} className={`px-3 py-1 rounded-full nb-border font-mono-sl text-[11px] uppercase ${REGION_CLS[d.region]} nb-hover`}>
                {d.name}
              </button>
            ))}
          </div>
        </NbCard>
      )}

      {/* MAP + PANEL */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-7">
          <div className="rounded-2xl nb-border nb-shadow-lg bg-white p-4 relative overflow-hidden" data-testid="megh-map-card">
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <div className="font-mono-sl text-[10px] uppercase">HOVER · LIVE WEATHER · CLICK TO OPEN</div>
              <div className="flex gap-2 flex-wrap items-center">
                <button
                  onClick={() => setTilted((v) => !v)}
                  data-testid="megh-3d-toggle"
                  className={`flex items-center gap-1 nb-border rounded-full px-3 py-1 font-mono-sl text-[10px] uppercase nb-hover ${tilted ? "bg-sl-primary nb-shadow" : "bg-white"}`}
                >
                  <Box className="w-3 h-3" />
                  {tilted ? "3D on" : "3D view"}
                </button>
                {Object.keys(REGION_CLS).map((r) => (
                  <div key={r} className="flex items-center gap-1 nb-border rounded-full px-2 py-0.5 bg-white">
                    <span className={`inline-block w-3 h-3 rounded-full ${REGION_CLS[r]} nb-border`} />
                    <span className="font-mono-sl text-[10px] uppercase">{r}</span>
                  </div>
                ))}
              </div>
            </div>

            <MeghalayaMap
              activeId={active?.id}
              onHover={onMapHover}
              onSelect={onMapSelect}
              tilted={tilted}
            />
          </div>
        </div>

        {/* DETAIL PANEL */}
        <div className="lg:col-span-5">
          <AnimatePresence mode="wait">
            {active && (
              <motion.div key={active.id} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -10 }} transition={{ duration: 0.25 }} data-testid="megh-detail">
                <NbCard color="primary" className="!p-5">
                  <Chip color="tertiary">{active.region.toUpperCase()}</Chip>
                  <h2 className="mt-2 font-heading font-black text-3xl tracking-tighter">{active.name}</h2>
                  <div className="font-mono-sl text-xs uppercase text-neutral-700">CAPITAL · {active.capital}</div>
                  <Link
                    to={`/meghalaya/${active.id}`}
                    data-testid={`megh-open-${active.id}`}
                    className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg nb-border nb-shadow nb-hover bg-black text-white font-heading font-bold text-sm uppercase"
                  >
                    Open district page <ArrowRight className="w-4 h-4" />
                  </Link>
                </NbCard>

                <NbCard className="!p-5 mt-3" data-testid="megh-problems">
                  <div className="flex items-center gap-2 mb-2"><AlertTriangle className="w-5 h-5 text-red-600" /><h3 className="font-heading font-black text-lg tracking-tight">Problems on the ground</h3></div>
                  <ul className="space-y-1.5">
                    {active.problems.map((p, i) => <li key={i} className="flex gap-2"><span className="text-red-600">•</span><span>{p}</span></li>)}
                  </ul>
                </NbCard>

                <NbCard color="secondary" className="!p-5 mt-3" data-testid="megh-achievements">
                  <div className="flex items-center gap-2 mb-2"><Trophy className="w-5 h-5" /><h3 className="font-heading font-black text-lg tracking-tight">Achievements & strengths</h3></div>
                  <ul className="space-y-1.5">
                    {active.achievements.map((p, i) => <li key={i} className="flex gap-2"><span>★</span><span>{p}</span></li>)}
                  </ul>
                </NbCard>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* TIMELINE */}
      {active && (
        <div className="mt-6" data-testid="megh-timeline">
          <div className="flex items-center gap-2 mb-3"><Calendar className="w-5 h-5" /><h3 className="font-heading font-black text-2xl tracking-tight">Govt actions taken · 2022 → 2026</h3></div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {[2022, 2023, 2024, 2025, 2026].map((yr) => {
              const items = active.initiatives.filter((i) => i.year === yr);
              return (
                <NbCard key={yr} color={items.length ? "accent" : "white"} className="!p-4 min-h-[140px] flex flex-col" data-testid={`year-${yr}`}>
                  <div className="font-heading font-black text-2xl tracking-tight">{yr}</div>
                  {items.length === 0 ? (
                    <div className="mt-2 text-neutral-500 text-sm italic">— no logged action —</div>
                  ) : (
                    <ul className="mt-2 space-y-2">
                      {items.map((it, i) => (
                        <li key={i}>
                          <div className="flex gap-2 items-start"><Lightbulb className="w-4 h-4 mt-0.5 shrink-0" /><div>
                            <div className="text-sm font-semibold">{it.title}</div>
                            <div className="font-mono-sl text-[10px] uppercase text-neutral-700 mt-0.5">BY · {it.by}</div>
                          </div></div>
                        </li>
                      ))}
                    </ul>
                  )}
                </NbCard>
              );
            })}
          </div>
        </div>
      )}

      {/* STUDENT IDEAS CORNER */}
      {active && (
        <div className="mt-8" data-testid="megh-ideas">
          <div className="flex items-center gap-2 mb-3"><MessageSquarePlus className="w-5 h-5" /><h3 className="font-heading font-black text-2xl tracking-tight">Student Ideas Corner · {active.name}</h3></div>
          <NbCard className="!p-5">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-1">
                <div className="font-mono-sl text-[10px] uppercase mb-2">PICK A QUESTION</div>
                <div className="flex flex-col gap-2" data-testid="prompt-list">
                  {prompts.map((p) => (
                    <button key={p.id} onClick={() => setPromptId(p.id)} data-testid={`prompt-${p.id}`} className={`text-left px-3 py-2 rounded-lg nb-border text-sm ${promptId === p.id ? "bg-sl-primary nb-shadow" : "bg-white"}`}>
                      {p.q}
                    </button>
                  ))}
                </div>
              </div>
              <div className="md:col-span-2">
                <div className="font-mono-sl text-[10px] uppercase mb-2">YOUR TAKE</div>
                {activePrompt && (
                  <div className="text-sm italic text-neutral-700 mb-2">"{activePrompt.q}"</div>
                )}
                <textarea data-testid="idea-body" rows={4} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Share how you would tackle this — be specific..." className="w-full px-4 py-3 nb-border rounded-lg bg-white" />
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  <NbButton onClick={submitIdea} color="black" disabled={submitting} data-testid="idea-submit">
                    {submitting ? "Sharing..." : "Share my idea"}
                  </NbButton>
                  <span className="font-mono-sl text-[10px] uppercase text-neutral-500">{body.length}/800</span>
                </div>
              </div>
            </div>

            <div className="mt-5" data-testid="idea-list">
              <div className="font-mono-sl text-[10px] uppercase mb-2">COMMUNITY IDEAS · {ideas.length}</div>
              {ideas.length === 0 && <div className="text-neutral-500 text-sm">Be the first to share an idea for {active.name}!</div>}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {ideas.map((i) => {
                  const p = prompts.find((x) => x.id === i.prompt_id);
                  return (
                    <NbCard key={i.id} className="!p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-8 rounded-full bg-sl-primary nb-border grid place-items-center">{i.avatar_emoji || "🚀"}</div>
                        <div className="text-sm font-semibold">{i.author}</div>
                        <button onClick={() => vote(i.id)} data-testid={`vote-${i.id}`} className="ml-auto flex items-center gap-1 text-sm nb-border px-2 py-1 rounded-full bg-white nb-hover"><ThumbsUp className="w-3 h-3" /> {i.votes}</button>
                      </div>
                      {p && <Chip color="tertiary">{p.id.toUpperCase()}</Chip>}
                      <p className="mt-2 text-sm">{i.body}</p>
                    </NbCard>
                  );
                })}
              </div>
            </div>
          </NbCard>
        </div>
      )}
    </Container>
  );
}
